import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { resourcesService } from '../../services/resources.service';
import LoadingState from '../../components/ui/LoadingState';

const { FiSave, FiArrowLeft, FiGlobe, FiAlertCircle } = FiIcons;

export default function ResourceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(id ? true : false);
  const [saving, setSaving] = useState(false);
  const [resource, setResource] = useState({
    slug: '',
    status: 'draft',
    category_id: null,
    trust_level: 'admin_reviewed'
  });
  
  const [translations, setTranslations] = useState({
    el: { title: '', summary: '', content: '' },
    en: { title: '', summary: '', content: '' }
  });

  const [activeLang, setActiveLang] = useState('el');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    try {
      const cats = await resourcesService.getCategories();
      setCategories(cats);

      if (id) {
        // Find existing resource
        const resources = await resourcesService.getAllResourcesAdmin();
        const res = resources.find(r => r.id === id);
        if (res) {
          setResource({
            slug: res.slug,
            status: res.status,
            category_id: res.category_id,
            trust_level: res.trust_level
          });
          
          const newTrans = { ...translations };
          res.translations?.forEach(t => {
            if (newTrans[t.language_code]) {
              newTrans[t.language_code] = {
                title: t.title || '',
                summary: t.summary || '',
                content: t.content || ''
              };
            }
          });
          setTranslations(newTrans);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let resourceId = id;
      if (id) {
        await resourcesService.updateResource(id, resource);
      } else {
        const newRes = await resourcesService.createResource(resource);
        resourceId = newRes.id;
      }

      // Save all translations
      for (const lang of Object.keys(translations)) {
        if (translations[lang].title) {
          await resourcesService.upsertTranslation({
            resource_id: resourceId,
            language_code: lang,
            ...translations[lang]
          });
        }
      }

      navigate('/admin/resources');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <button 
        onClick={() => navigate('/admin/resources')}
        className="flex items-center text-slate-500 hover:text-slate-900 font-bold mb-8 transition-colors"
      >
        <SafeIcon icon={FiArrowLeft} className="mr-2" />
        Back to Resources
      </button>

      <form onSubmit={handleSave} className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            {id ? 'Edit Resource' : 'New Resource'}
          </h1>
          <button 
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-black rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 disabled:opacity-50"
          >
            <SafeIcon icon={FiSave} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Config */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-6">
              <h3 className="text-lg font-black text-slate-900">Settings</h3>
              
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">URL Slug</label>
                <input 
                  type="text" required
                  value={resource.slug}
                  onChange={e => setResource({...resource, slug: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 outline-none font-bold"
                  placeholder="e.g. skin-care-guide"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Category</label>
                <select 
                  value={resource.category_id || ''}
                  onChange={e => setResource({...resource, category_id: e.target.value || null})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 outline-none font-bold appearance-none"
                >
                  <option value="">Uncategorized</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.slug}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {['draft', 'published'].map(s => (
                    <button
                      key={s} type="button"
                      onClick={() => setResource({...resource, status: s})}
                      className={`py-3 rounded-xl text-xs font-black uppercase tracking-widest border transition-all ${
                        resource.status === s 
                        ? 'bg-slate-900 border-slate-900 text-white' 
                        : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Translations */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
              <div className="flex border-b border-slate-100">
                {Object.keys(translations).map(lang => (
                  <button
                    key={lang} type="button"
                    onClick={() => setActiveLang(lang)}
                    className={`flex-1 py-4 text-xs font-black uppercase tracking-widest transition-all ${
                      activeLang === lang 
                      ? 'bg-white text-blue-600 border-b-2 border-blue-600' 
                      : 'bg-slate-50 text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <SafeIcon icon={FiGlobe} className="inline mr-2" />
                    {lang === 'el' ? 'Greek' : 'English'}
                  </button>
                ))}
              </div>

              <div className="p-8 space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Title ({activeLang})</label>
                  <input 
                    type="text"
                    value={translations[activeLang].title}
                    onChange={e => setTranslations({
                      ...translations,
                      [activeLang]: { ...translations[activeLang], title: e.target.value }
                    })}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 outline-none text-xl font-black text-slate-900"
                    placeholder="Enter title..."
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Summary ({activeLang})</label>
                  <textarea 
                    rows="3"
                    value={translations[activeLang].summary}
                    onChange={e => setTranslations({
                      ...translations,
                      [activeLang]: { ...translations[activeLang], summary: e.target.value }
                    })}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 outline-none font-medium text-slate-700"
                    placeholder="Brief description..."
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Content Markdown ({activeLang})</label>
                  <textarea 
                    rows="15"
                    value={translations[activeLang].content}
                    onChange={e => setTranslations({
                      ...translations,
                      [activeLang]: { ...translations[activeLang], content: e.target.value }
                    })}
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 outline-none font-mono text-sm text-slate-700"
                    placeholder="# Use Markdown here..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}